import { ChangeEvent, FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faArchive, faChevronDown, faFilter } from "@fortawesome/pro-regular-svg-icons";

import { useNavigate, useSearchParams } from "react-router-dom";

import { ReactComponent as Plus } from "@assets/icons/plus.svg";
import { ReactComponent as MagnifyingGlass } from "@assets/icons/list-builder/magnifying-glass.svg";

import {
	useArchiveListMutation,
	useDeleteListMutation,
	useDeleteListsMutation,
	useDuplicateListMutation,
	useLazyGetLeanListsQuery,
	useLazyGetListQuery,
	useLazyDownloadSelectedListQuery,
	useResetListMutation,
	useUpdateListMutation,
} from "@/pages/Private/redux/list/list.api";
import { buildListOverviewRequest } from "@/pages/Private/redux/list/lean-lists.params";
import { useGetListOwnershipCountsQuery } from "@/pages/Private/redux/list/list-ownership-counts.api";

import { Icon, LoadingOverlay, PermissionCheck } from "@/components";
import { useSearch } from "@/utils";

import { PermissionRoles } from "@/enum";

import { InputField } from "@/components/InputField/InputField";

import {
	ArchivedView,
	ListContentFilter,
	ListOwnership,
	ListStatus,
	UpdateType,
} from "@/enum/list.enum";

import { useStickyParams } from "@/utils/useStickyParams";

import { Button } from "@/components/Button/Button";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { Table } from "@/components/Table/Table";
import { listSelector, setListSorting } from "@/pages/Private/redux/list/list.slice";
import { Order, SortParams } from "@/types/types";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { SvgIcon } from "@/components/Icon/SvgIcon";

import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";

import { useLazyGetCompaniesQuery } from "@/pages/Private/redux/companies/companies.api";

import {
	AutoComplete,
	AutoCompleteItem as AutoCompleteItemType,
} from "@/components/AutoComplete/AutoComplete";

import { authSelector } from "@/pages/Public/redux/auth.slice";

import { useHasPermissions } from "@/utils/useHasPermissions";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { useSnackBar } from "@/utils/useSnackbar";

import { reportActivationFailure } from "../utils/listRefusalMessage";

import {
	ListOwnershipTabs,
	SEARCH_RESULTS_TAB,
	ListTab,
	ListOwnershipCountMap,
} from "./ListOwnershipTabs";
import { ALL_SOURCES, SourceTypePalette } from "./SourceTypePalette";
import { PinFilterButton, PinSummaryItem } from "./PinFilterButton";
import { useListTableColumns } from "./useListTableColumns";
import { MyListsEmptyState } from "./MyListsEmptyState";
import { ListOverviewModals } from "./ListOverviewModals";

import { getSourceFilterEntry } from "../config/sourceTypeFilterCatalog";
import { DownloadFile, List, UpdateList } from "../schema/list";
import { Companies } from "../../Companies/schema/companies";
import { FeaturesCodes } from "../../Users/enum/codes";
import { useCampaignStatusPolling } from "../hooks/useCampaignStatusPolling";
import { useListCampaignSummaries } from "./ListCampaignSubRows";
import { useListOverviewPinnedTabs } from "../hooks/useListOverviewPinnedTabs";
import { useListPinnedTabToasts } from "../hooks/useListPinnedTabToasts";
import {
	arePinnedFiltersEqual,
	findMatchingPinnedTabId,
	isFilterPinnable,
	PinnedFilterTab,
	PinnedTabFilters,
} from "../utils/pinnedFilterTabs";
import {
	buildListOverviewSearch,
	getArchivedViewFromUrl,
	getContentFilterFromUrl,
	getListTypeFromUrl,
	getOwnershipFromUrl,
	getSearchOwnershipFromUrl,
	getSearchTermFromUrl,
	isSearchActiveFromUrl,
} from "../utils/listOverviewUrlParams";
import { shouldShowMyListsEmptyState } from "../utils/listOverviewEmptyState";
import { listLifecycleActions } from "../utils/listLifecycleActions";

export const ListTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`list.${key}`), [t]);

	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);

	const [searchParams] = useSearchParams();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
	const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false);
	const [showDeleteListsModal, setShowDeleteListsModal] = useState<boolean>(false);
	const [showResetModal, setShowResetModal] = useState<boolean>(false);
	const [selectedList, setSelectedList] = useState<List | null>(null);

	const [copyCustomColumns, setCopyCustomColumns] = useState<boolean>(false);
	const { searchValue, setSearchValue, query, setQuery } = useSearch(getSearchTermFromUrl());
	const [deleteList] = useDeleteListMutation();
	const [deleteLists] = useDeleteListsMutation();
	const [resetList, { isLoading: resetListIsLoading }] = useResetListMutation();
	const [downloadSelectedList, { isFetching: downloadSelectedListIsLoading }] =
		useLazyDownloadSelectedListQuery();
	const [archiveList] = useArchiveListMutation();
	const [duplicateList, { isLoading: duplicateListIsLoading, error: duplicateListError }] =
		useDuplicateListMutation();

	const [update] = useUpdateListMutation();
	const handleSorting = (sortParams: SortParams) => dispatch(setListSorting(sortParams));
	const { sortingList, lists: data, listMeta } = useAppSelector(listSelector);
	const { setSearchValue: setCompanySearchValue, query: companyQuery } = useSearch();
	const [company, setCompany] = useState<AutoCompleteItemType>();
	const { user } = useAppSelector(authSelector);
	const { hasPermissions } = useHasPermissions();

	useCampaignStatusPolling();

	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	// Expandable campaign sub-rows + Campaigns bulk button: per-list summaries (eager-loaded for
	// folded into the lean list response) plus per-campaign and bulk run-status toggles.
	const { getSubRows, getSummaries, toggleCampaign, bulkToggle, isBulkPending } =
		useListCampaignSummaries(data, {
			// A campaign status change that fails has no other way to reach the user (PREC-4129).
			onStatusChangeFailed: (count) =>
				handleOpenSnackbar(t("list.campaignBulk.statusChangeFailed", { count })),
		});

	// User-pinned filter tabs (saved structured-filter snapshots), persisted in localStorage.
	const { pinnedTabs, addPinnedTab, renamePinnedTab, updatePinnedTabFilters, removePinnedTab } =
		useListOverviewPinnedTabs();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const triggerRef = useRef<any>();

	const [companies, setCompanies] = useState<Companies[]>([]);

	const [contentFilter, setContentFilter] = useState<ListContentFilter>(getContentFilterFromUrl());
	const [archivedView, setArchivedView] = useState<ArchivedView>(getArchivedViewFromUrl());

	const [ownership, setOwnership] = useState<ListOwnership>(getOwnershipFromUrl());

	// "Search Results" is a separate tab axis: when active it searches across all ownership
	// scopes, optionally narrowed by `searchOwnership`, independently of the ownership tabs.
	const [isSearchTab, setIsSearchTab] = useState<boolean>(isSearchActiveFromUrl());
	const [searchOwnership, setSearchOwnership] = useState<ListOwnership>(
		getSearchOwnershipFromUrl()
	);

	const [getCompanies] = useLazyGetCompaniesQuery();

	useEffect(() => {
		if (hasPermissions([PermissionRoles.GLOBAL_ADMIN]) && companyQuery) {
			getCompanies({
				page: 1,
				limit: 10,
				searchValue: companyQuery,
			})
				.unwrap()
				.then((res) => {
					setCompanies(res.data);
				});
		}
	}, [companyQuery]);

	const [newName, setNewName] = useState<string>("");

	const [selectedFiles, setSelectedFiles] = useState<DownloadFile[]>([]);
	const [selectAll, setSelectAll] = useState<boolean>(false);
	const clearBulkSelection = useCallback(() => {
		setSelectedFiles([]);
		setSelectAll(false);
	}, []);

	useEffect(() => {
		setNewName(selectedList?.name ?? "");
	}, [selectedList]);

	const [selectedSourceType, setSelectedSourceType] = useState<string | undefined>(
		getListTypeFromUrl()
	);
	const [showSourcePalette, setShowSourcePalette] = useState(false);

	useEffect(() => {
		clearBulkSelection();
	}, [clearBulkSelection, contentFilter, archivedView, selectedSourceType, sortingList, query]);

	// Open the source-type palette with ⌘K / Ctrl+K, mirroring the command-palette convention.
	useEffect(() => {
		const handleHotkey = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
				event.preventDefault();
				setShowSourcePalette(true);
			}
		};

		window.addEventListener("keydown", handleHotkey);

		return () => window.removeEventListener("keydown", handleHotkey);
	}, []);

	const [localLoading, setLocalLoading] = useState(false);
	const [page, setPage] = useState(1);

	useEffect(() => {
		if (searchParams.has("list")) {
			const cListName = searchParams.get("list");

			if (cListName) {
				setSearchValue(cListName);
			}
		}
	}, [searchParams, setSearchValue]);

	// Reveal/activate the "Search Results" tab whenever a search term is present, and fall back
	// to the ownership tabs once the term is cleared. Keyed on `searchValue` only, so manually
	// switching to an ownership tab while a term exists is not immediately undone.
	useEffect(() => {
		setIsSearchTab(!!searchValue.trim());
	}, [searchValue]);

	useStickyParams("list", setSearchValue, searchValue);

	const [sortState, setSortState] = useState({ sortBy: "", sortOrder: 0 });

	useEffect(() => {
		if (sortingList.sortBy && sortingList.sortOrder) {
			let cSortOrder = 0;

			if (sortingList.sortOrder === Order.ASC) {
				cSortOrder = 1;
			}

			if (sortingList.sortOrder === Order.DESC) {
				cSortOrder = 2;
			}

			setSortState({
				sortBy: sortingList.sortBy,
				sortOrder: cSortOrder,
			});
		}
	}, [sortingList]);

	const [getLeanLists, { isLoading, isFetching }] = useLazyGetLeanListsQuery();

	// PREC-3623: per-ownership list counts for the tab number bubbles. Mirrors the inbox
	// reply-counts pattern (read straight from the hook); refetched on the broad `List` tag.
	const { data: ownershipCounts } = useGetListOwnershipCountsQuery();
	const ownershipCountMap: ListOwnershipCountMap = {
		[ListOwnership.MINE]: ownershipCounts?.mine,
		[ListOwnership.TEAM]: ownershipCounts?.team,
		[ListOwnership.ALL]: ownershipCounts?.all,
	};

	useEffect(() => {
		if (triggerRef.current) {
			triggerRef.current.abort();
		}
	}, [searchValue]);

	useEffect(() => {
		const fetchNewLists = async () => {
			// On the Search Results tab the ownership tabs are ignored; the scope comes from the
			// optional `searchOwnership` dropdown (default ALL → no ownership filter).
			const effectiveOwnership = isSearchTab ? searchOwnership : ownership;

			triggerRef.current = getLeanLists(
				buildListOverviewRequest({
					page,
					query,
					sourceType: selectedSourceType !== "ALL" ? selectedSourceType : undefined,
					sorting: sortingList,
					archivedView,
					contentFilter,
					ownership: effectiveOwnership,
				})
			);

			try {
				await triggerRef.current.unwrap();
				setLocalLoading(false);
			} catch (error) {
				if ((error as Error).name === "AbortError") {
					return;
				}

				console.error(error);
			} finally {
				setLocalLoading(false);
			}
		};

		fetchNewLists();
	}, [
		query,
		contentFilter,
		archivedView,
		selectedSourceType,
		ownership,
		isSearchTab,
		searchOwnership,
		sortingList,
		page,
		getLeanLists,
	]);

	// PREC-3623: when the *unfiltered* My Lists scope is empty, show the onboarding empty state
	// (instead of the previous auto-switch to All lists). Decision extracted to a pure predicate.
	const showMyListsEmptyState = shouldShowMyListsEmptyState({
		ownership,
		isSearchTab,
		hasSearchQuery: !!query.trim(),
		contentFilter,
		archivedView,
		hasSourceFilter: !!selectedSourceType && selectedSourceType !== ALL_SOURCES,
		isLoading,
		isFetching,
		itemCount: listMeta?.itemCount ?? 0,
	});

	// Poll every 5 seconds for each list that has STOPPING status (fetch individually by ID)
	const [getList] = useLazyGetListQuery();

	useEffect(() => {
		const stoppingLists = data.filter((list) => list.status === ListStatus.STOPPING);

		if (stoppingLists.length === 0) {
			return;
		}

		const interval = setInterval(() => {
			stoppingLists.forEach((list) => {
				if (list.id) {
					getList(list.id);
				}
			});
		}, 5000);

		return () => clearInterval(interval);
	}, [data, getList]);

	const handleSelectFile = (file: DownloadFile) => {
		setSelectedFiles((prevSelectedFiles) => {
			const fileExists = prevSelectedFiles.some((selectedFile) => selectedFile.id === file.id);

			if (fileExists) {
				return prevSelectedFiles.filter((selectedFile) => selectedFile.id !== file.id);
			} else {
				return [...prevSelectedFiles, file];
			}
		});
	};

	const handleDownloadSelected = async (file?: DownloadFile[]) => {
		try {
			let name = "listexport.xlsx";

			if (file || selectedFiles.length === 1) {
				name = file?.[0]?.name ?? selectedFiles[0].name;
			}

			if (
				!(
					// @ts-expect-error - showSaveFilePicker is not typed
					window.showSaveFilePicker
				)
			) {
				const downloadPromise = await downloadSelectedList({
					files: file ?? selectedFiles,
				}).unwrap();

				const url = URL.createObjectURL(downloadPromise);

				const a = document.createElement("a");

				a.href = url;

				a.download = name;
				a.rel = "noopener";

				// Firefox is happier when the element is in the DOM
				document.body.appendChild(a);
				a.click();
				a.remove();

				// Revoke the URL after a tick to let the download start
				setTimeout(() => URL.revokeObjectURL(url), 1000);
				clearBulkSelection();
			} else {
				const downloadPromise = downloadSelectedList({
					files: file ?? selectedFiles,
				}).unwrap();

				window
					// @ts-expect-error - showSaveFilePicker is not typed
					.showSaveFilePicker({
						suggestedName: name,
						types: [
							{
								description: "My File",
								accept: {
									["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"]: [`.xlsx`],
								},
							},
						],
					})
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					.then((handle: { createWritable: () => Promise<any> }) =>
						handle.createWritable().then(async (writable) => {
							const blob = await downloadPromise;

							await writable.write(blob);
							await writable.close();
						})
					) // eslint-disable-next-line @typescript-eslint/no-explicit-any
					.catch((error: any) => {
						console.error(error);
					})
					.finally(() => {
						clearBulkSelection();
					});
			}
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		if (selectAll) {
			setSelectedFiles(data?.map((item) => ({ id: item.id, name: item.name })) || []);
		}
	}, [selectAll, data]);

	const toggleSelectAll = () => {
		if (selectAll) {
			clearBulkSelection();

			return;
		}

		setSelectAll(true);
	};

	const handleToggle = useCallback(
		async (id: number, status: ListStatus) => {
			const updater: Partial<UpdateList> = {
				id: id,
				updateType: UpdateType.STATUS,
			};

			if (status === ListStatus.ACTIVE) {
				updater.status = ListStatus.PAUSED;
			} else if (status === ListStatus.PAUSED) {
				updater.status = ListStatus.ACTIVE;
			}

			try {
				await update({ ...updater }).unwrap();
			} catch (error) {
				reportActivationFailure(error, ts("status.stoppingError"), handleOpenSnackbar);
			}
		},
		[update, handleOpenSnackbar, ts]
	);

	const handleResetModal = (row: List) => {
		setShowResetModal(true);
		setSelectedList(row);
	};

	const handleDeleteModal = (row: List) => {
		setShowDeleteModal(true);
		setSelectedList(row);
	};
	const handleArchiveModal = (row: List) => {
		setSelectedList(row);
		setShowArchiveModal(true);
	};

	const handleDuplicateModal = (row: List) => {
		setSelectedList(row);
		setShowDuplicateModal(true);
	};
	const tableColumns = useListTableColumns({
		notify: handleOpenSnackbar,
		selectAll,
		toggleSelectAll,
		selectedFiles,
		handleSelectFile,
		sortState,
		handleToggle,
		isSequencerActive: !!isSequencerActive,
		isBulkPending,
		getSummaries,
		bulkToggle,
		toggleCampaign,
		navigate,
		handleDownloadSelected,
		handleResetModal,
		handleArchiveModal,
		handleDeleteModal,
		handleDuplicateModal,
	});
	const { handleArchiveSelected, handleDeleteSelected, handleDeleteList, handleArchiveList } =
		listLifecycleActions({
			selectedFiles,
			selectedList,
			archiveList,
			deleteLists,
			deleteList,
			notify: handleOpenSnackbar,
			clearBulkSelection,
			setPage,
			setShowDeleteListsModal,
			setShowDeleteModal,
			setShowArchiveModal,
		});

	const handleDuplicateList = async () => {
		try {
			await duplicateList({
				id: selectedList?.id ?? 0,
				name: newName,
				copyCustomColumns: copyCustomColumns,
			}).unwrap();
			setShowDuplicateModal(false);
			setNewName("");
			setCompany(undefined);
		} catch (err) {
			console.error(err);
		}
	};

	const handleResetList = async () => {
		try {
			await resetList(selectedList?.id ?? 0).unwrap();
			setShowResetModal(false);
		} catch (err) {
			console.error(err);
		}
	};

	useEffect(() => {
		setPage(1);
		navigate(
			{
				search: buildListOverviewSearch(location.search, {
					contentFilter,
					archivedView,
					selectedSourceType,
					ownership,
					searchOwnership,
				}),
			},
			{ replace: true }
		);
	}, [
		contentFilter,
		archivedView,
		location.search,
		navigate,
		selectedSourceType,
		ownership,
		searchOwnership,
	]);

	const handleSelectSourceType = (id: string) => {
		setSelectedSourceType(id);
	};

	const activeTab: ListTab = isSearchTab ? SEARCH_RESULTS_TAB : ownership;

	// Built-in ownership tab → a clean scope view (clears the structured filters + search). This
	// keeps the bare ownership tabs visually distinct from the saved pinned-filter tabs, which
	// each carry a full structured-filter snapshot.
	const handleTabChange = (tab: ListTab) => {
		if (tab === SEARCH_RESULTS_TAB) {
			setIsSearchTab(true);

			return;
		}

		setIsSearchTab(false);
		setOwnership(tab);
		setContentFilter(ListContentFilter.ALL);
		setArchivedView(ArchivedView.HIDE);
		setSelectedSourceType(ALL_SOURCES);
		setSearchValue("");
		setPage(1);
	};

	// --- Pin-to-tab: snapshot the current filters and reconcile against the user's pinned tabs.
	// A free-text search term is a pinnable criterion too; while searching, the effective scope is
	// the Search Results scope (`searchOwnership`) rather than the ownership tab. ---
	const normalizedSourceType =
		selectedSourceType && selectedSourceType !== ALL_SOURCES ? selectedSourceType : undefined;

	const searchTerm = searchValue.trim();
	const effectiveScope = isSearchTab ? searchOwnership : ownership;

	const currentFilters: PinnedTabFilters = {
		ownership: effectiveScope,
		contentFilter,
		archivedView,
		sourceType: normalizedSourceType,
		search: searchTerm || undefined,
	};

	const activePinnedTabId = findMatchingPinnedTabId(pinnedTabs, currentFilters) ?? null;

	const pinSummaryItems: PinSummaryItem[] = [
		...(searchTerm ? [{ label: ts("pinTab.criteria.search"), value: searchTerm }] : []),
		...(effectiveScope !== ListOwnership.MINE
			? [
					{
						label: ts("pinTab.criteria.scope"),
						value: ts(`ownership.${effectiveScope.toLowerCase()}`),
					},
				]
			: []),
		...(contentFilter !== ListContentFilter.ALL
			? [{ label: ts("pinTab.criteria.show"), value: ts(`filter.${contentFilter}`) }]
			: []),
		...(normalizedSourceType
			? [{ label: ts("pinTab.criteria.source"), value: getSourceTypeText() }]
			: []),
		...(archivedView !== ArchivedView.HIDE
			? [{ label: ts("pinTab.criteria.archived"), value: ts("pinTab.criteria.archivedValue") }]
			: []),
	];

	const suggestedPinName = pinSummaryItems.length
		? pinSummaryItems.map((item) => item.value).join(" · ")
		: ts("pinTab.defaultName");

	const applyPinnedFilters = (filters: PinnedTabFilters) => {
		const restoredSearch = filters.search?.trim() ?? "";

		setContentFilter(filters.contentFilter);
		setArchivedView(filters.archivedView);
		setSelectedSourceType(filters.sourceType ?? ALL_SOURCES);
		setSearchValue(restoredSearch);
		// Apply the search term to the *debounced* query immediately so the first fetch after the
		// click is already the filtered one. Relying on the 800ms debounce here would fire an
		// intermediate all-lists fetch (empty query) that can resolve last and clobber the results.
		setQuery(restoredSearch);

		// A pinned search restores the Search Results axis (scope = the pinned `ownership`); a
		// non-search pin restores the ownership tab. `isSearchTab` is also derived from the search
		// term via effect, but set it explicitly to avoid a flicker on the intermediate render.
		if (restoredSearch) {
			setIsSearchTab(true);
			setSearchOwnership(filters.ownership);
		} else {
			setIsSearchTab(false);
			setOwnership(filters.ownership);
		}

		setPage(1);
	};

	const handleSelectPinnedTab = (tab: PinnedFilterTab) => applyPinnedFilters(tab.filters);

	const {
		handlePinCurrentView,
		handleUpdatePinnedTab,
		handleDeletePinnedTab,
		handleRenamePinnedTab,
	} = useListPinnedTabToasts({
		mutations: {
			pinnedTabs,
			addPinnedTab,
			renamePinnedTab,
			updatePinnedTabFilters,
			removePinnedTab,
		},
		currentFilters,
		notify: handleOpenSnackbar,
		t,
	});

	const handleFetchMore = () => {
		if (selectAll) {
			toggleSelectAll();
		}

		setPage(page + 1);
	};

	return isLoading ? (
		<LoadingOverlay />
	) : (
		<div className="w-full">
			<ListOwnershipTabs
				activePinnedTabId={activePinnedTabId}
				counts={ownershipCountMap}
				pinnedTabs={pinnedTabs}
				showSearchTab={!!searchTerm && !activePinnedTabId}
				value={activeTab}
				onChange={handleTabChange}
				onDeletePinnedTab={handleDeletePinnedTab}
				onRenamePinnedTab={handleRenamePinnedTab}
				onSelectPinnedTab={handleSelectPinnedTab}
				onUpdatePinnedTab={handleUpdatePinnedTab}
			/>
			<div className="flex items-center gap-2 h-11 px-4 bg-white border-b border-border-light">
				<div className="w-[196px] flex-shrink-0">
					<InputField
						className="!rounded-[10px]"
						classNameWrapper="!mb-0"
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setLocalLoading(true);
							setSearchValue(e.target.value);
						}}
						iconInside={<SvgIcon className="text-gray-500" svgIcon={MagnifyingGlass} />}
						name={"search"}
						placeholder={ts("search")}
						size={ButtonSize.XS}
						value={searchValue}
					/>
				</div>
				{isSearchTab && (
					<div className="flex-shrink-0">
						<Dropdown
							data={[
								{ title: ts("ownership.all"), id: ListOwnership.ALL },
								{ title: ts("ownership.mine"), id: ListOwnership.MINE },
								{ title: ts("ownership.team"), id: ListOwnership.TEAM },
							]}
							defaultValue={{
								title: ts(`ownership.${searchOwnership.toLowerCase()}`),
								id: searchOwnership,
							}}
							floating={true}
							handleSelect={(value: AutoCompleteItem) => {
								setSearchOwnership(value.id as ListOwnership);
							}}
							size={ButtonSize.XS}
							title={ts("ownership.scope")}
							value={{
								title: ts(`ownership.${searchOwnership.toLowerCase()}`),
								id: searchOwnership,
							}}
						/>
					</div>
				)}
				<div className="ml-auto flex items-center gap-2">
					<div className="flex-shrink-0">
						<Dropdown
							data={[
								{ title: ts("filter.ALL"), id: ListContentFilter.ALL },
								{ title: ts("filter.WITH_LEADS"), id: ListContentFilter.WITH_LEADS },
								{ title: ts("filter.WITH_CAMPAIGNS"), id: ListContentFilter.WITH_CAMPAIGNS },
								{
									title: ts("filter.WITH_ACTIVE_CAMPAIGNS"),
									id: ListContentFilter.WITH_ACTIVE_CAMPAIGNS,
								},
							]}
							// Size the menu to its widest option (with a floor) so the longer labels
							// (e.g. "Only lists with active campaigns") don't wrap; the trigger stays compact.
							classNameDropdown="w-max min-w-[200px]"
							defaultValue={{
								title: ts(`filter.${contentFilter}`),
								id: contentFilter,
							}}
							floating={true}
							icon={<Icon className="w-3.5 h-3.5" icon={faFilter} />}
							handleSelect={(value: AutoCompleteItem) => {
								setContentFilter(value.id as ListContentFilter);
							}}
							size={ButtonSize.XS}
							value={{
								title: ts(`filter.${contentFilter}`),
								id: contentFilter,
							}}
						/>
					</div>
					<div className="flex-shrink-0">
						<Dropdown
							data={[
								{ title: ts("archivedView.HIDE"), id: ArchivedView.HIDE },
								{ title: ts("archivedView.SHOW"), id: ArchivedView.SHOW },
							]}
							defaultValue={{
								title: ts(`archivedView.${archivedView}`),
								id: archivedView,
							}}
							floating={true}
							icon={<Icon className="w-3.5 h-3.5" icon={faArchive} />}
							handleSelect={(value: AutoCompleteItem) => {
								setArchivedView(value.id as ArchivedView);
							}}
							size={ButtonSize.XS}
							value={{
								title: ts(`archivedView.${archivedView}`),
								id: archivedView,
							}}
						/>
					</div>
					<div className="flex-shrink-0">
						<button
							className="flex h-[32px] cursor-pointer items-center gap-1.5 rounded-[10px] border border-gray-200 bg-white px-3 text-xs font-semibold text-text duration-200 hover:border-brand-light hover:shadow-sm"
							type="button"
							onClick={() => setShowSourcePalette(true)}
						>
							<span className="text-text-meta">{ts("sourceFilter.trigger")}:</span>
							<span className="max-w-[140px] truncate">{getSourceTypeText()}</span>
							<Icon className="h-3 w-3 text-text-meta" icon={faChevronDown} />
							<span className="ml-0.5 rounded border border-border-light px-1 py-px text-xxs font-medium text-text-disabled">
								⌘K
							</span>
						</button>
						<SourceTypePalette
							isOpen={showSourcePalette}
							selectedSourceType={selectedSourceType}
							onClose={() => setShowSourcePalette(false)}
							onSelect={handleSelectSourceType}
						/>
					</div>
					<PinFilterButton
						disabled={!isFilterPinnable(currentFilters) || activePinnedTabId !== null}
						suggestedName={suggestedPinName}
						summaryItems={pinSummaryItems}
						onPin={handlePinCurrentView}
					/>
					<PermissionCheck requiredPermissions={[PermissionRoles.LIST]}>
						<Button
							color={ButtonColor.ACTION}
							image={<SvgIcon className="w-[16px] h-[16px] mr-2" svgIcon={Plus} />}
							size={ButtonSize.S}
							testId="new-user-button"
							title={`New List`}
							onClick={() => navigate("/app/lists/create")}
						/>
					</PermissionCheck>
				</div>
			</div>

			<div className="relative w-full">
				<div className="pr-4 pb-[20px]">
					<Table
						canExpand={(row: List) => Number(row.campaignCount) > 0}
						columns={tableColumns}
						currentlyDownloadingSelected={downloadSelectedListIsLoading || false} //for export
						data={data || []}
						getSubRows={(row: List) => getSubRows(row.id)}
						handleArchive={() => {
							handleArchiveSelected(); //for archive
						}}
						handleDelete={() => {
							setShowDeleteListsModal(true);
						}}
						handleExport={() => {
							handleDownloadSelected(); //for export
						}}
						handleFetchMore={() => {
							if (
								listMeta?.itemCount &&
								data.length < listMeta?.itemCount &&
								!isFetching &&
								!localLoading &&
								page < listMeta.pageCount
							) {
								handleFetchMore();
							}
						}}
						handleSorting={(sortParams: SortParams) => {
							setLocalLoading(true);
							setPage(1);
							handleSorting(sortParams);
						}}
						hideColumnsCount={true}
						hidePagination={true}
						isFetching={isFetching}
						isLoading={isLoading}
						lazyLoadPagination={true}
						noDataText={
							showMyListsEmptyState ? (
								<MyListsEmptyState
									onCreateList={() => navigate("/app/lists/create")}
									onSeeAllLists={() => handleTabChange(ListOwnership.ALL)}
								/>
							) : undefined
						}
						overallCount={listMeta.itemCount}
						page={page}
						clearSelection={clearBulkSelection}
						selectedCount={selectedFiles.length}
						showFooterFunctions={selectedFiles.length > 0}
						sticky={true}
						totalPages={listMeta.pageCount}
					/>
				</div>
			</div>
			<ListOverviewModals
				copyCustomColumns={copyCustomColumns}
				duplicateError={duplicateListError}
				duplicateLoading={duplicateListIsLoading}
				newName={newName}
				resetLoading={resetListIsLoading}
				selectedList={selectedList}
				showArchiveModal={showArchiveModal}
				showDeleteListsModal={showDeleteListsModal}
				showDeleteModal={showDeleteModal}
				showDuplicateModal={showDuplicateModal}
				showResetModal={showResetModal}
				onCloseArchive={() => setShowArchiveModal(false)}
				onCloseDelete={() => setShowDeleteModal(false)}
				onCloseDeleteLists={() => setShowDeleteListsModal(false)}
				onCloseDuplicate={() => setShowDuplicateModal(false)}
				onCloseReset={() => setShowResetModal(false)}
				onConfirmArchive={handleArchiveList}
				onConfirmDelete={handleDeleteList}
				onConfirmDeleteLists={handleDeleteSelected}
				onConfirmDuplicate={handleDuplicateList}
				onConfirmReset={handleResetList}
				onNewNameChange={(value) => setNewName(value)}
				onToggleCopyCustomColumns={() => setCopyCustomColumns(!copyCustomColumns)}
			/>
			{renderMessageSnackbar()}
		</div>
	);

	function getSourceTypeText(): string {
		if (!selectedSourceType || selectedSourceType === ALL_SOURCES) {
			return t("list.sourceFilter.all");
		}

		const entry = getSourceFilterEntry(selectedSourceType);

		return entry ? t(entry.labelKey) : t("list.sourceFilter.all");
	}
};
